import { Logger } from 'loglevel';
import { ApiClients } from './BaseApiClient';
import { Config, Options, Metadata, RestOptions, StreamOptions } from './types';
import { tap, catchError } from 'rxjs/operators';
import { AxiosResponse } from 'axios';

interface LunaError {
  type: string;
  details: {
    message: string;
  };
}

const sessionRevokedException =
  'com.unitedtraders.luna.platform2.security.server.exception.SessionRevokedException';
const cancelSubscriptionOnLogoutException =
  'com.unitedtraders.luna.platform2.web.exceptions.CancelSubscriptionOnLogoutException';

export class BaseService {
  protected clientName: string = 'BaseService';
  protected logger: Logger;
  protected clients: ApiClients;
  protected config: Config;
  protected removeTokens?: () => void;

  constructor(clients: ApiClients, logger: Logger, schema: any, options: Config | Options) {
    this.logger = logger;
    this.clients = clients;

    // @ts-ignore
    if (options.config) {
      const actualOptions = options as Options;
      this.config = actualOptions.config;
      this.removeTokens = actualOptions.removeTokens;
    } else {
      const config = options as Config;
      this.config = config;
    }
  }

  protected createRest<T, R>(
    name: string,
    isAuthorized: boolean,
    requestSchema: string,
    responseSchema: string,
    fancyName: string
  ) {
    return (request: R, metadata?: Metadata, options?: RestOptions) => {
      const client = isAuthorized ? this.clients.securedRest : this.clients.rest;

      this.logger.debug(`${fancyName}: Request:`, request);

      const response = client!.send<T>(name, request, metadata);

      return response
        .then<AxiosResponse<T>>(data => {
          this.logger.debug(`${fancyName}: Response:`, data.data);

          return data;
        })
        .catch(error => {
          const responseBody =
            typeof error.response?.data === 'object'
              ? JSON.stringify(error.response?.data)
              : error.response?.data;
          const requestBody = typeof request === 'object' ? JSON.stringify(request) : request;
          const msg =
            `${fancyName}: Error in request to url=${name}, response status=${error.response?.status}, ` +
            `response body=${responseBody}, ` +
            `request=${requestBody}, error message=${error.message}`;
          this.logger.debug(msg);
          throw error;
        });
    };
  }

  protected createStream<T, R, M>(
    name: string,
    isAuthorized: boolean,
    requestSchema: string,
    responseSchema: string,
    fancyName: string
  ) {
    return (request: R, metadata?: M, options?: StreamOptions) => {
      this.logger.debug(`${fancyName}: Subscribe:`, request);

      return this.clients
        .stream!.topic<T>({
          parameters: request,
          name,
          metadata,
          isAuthorized,
          isRaw: options && options.isRaw
        })
        .pipe<T, T>(
          tap(data => {
            this.logger.debug(`${fancyName}: Response:`, data);
          }),
          catchError((error: LunaError) => {
            if (
              error.type === sessionRevokedException ||
              error.type === cancelSubscriptionOnLogoutException
            ) {
              this.removeTokens?.();
            }
            throw error;
          })
        );
    };
  }
}
